import { ChangeEvent, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Checkbox, Fade, FormControlLabel, Typography } from '@mui/material';
import { Box } from '@mui/system';

import BasicModal from 'components/Common/BasicModal';
import PrimaryButton from 'components/Common/PrimaryButton';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setPrivacyPolicyModalOpen } from 'store/global/globalSlice';
import { t } from 'i18next';
import parse from 'html-react-parser';

const PrivacyPolicyModal = () => {
    const [cookies, setCookies] = useCookies(['raia_privacy_policy_read_date']);
    const [checkbox, setCheckbox] = useState(!!cookies?.raia_privacy_policy_read_date);
    const [isContentRead, setIsContentRead] = useState(!!cookies?.raia_privacy_policy_read_date);
    const contentRef = useRef<HTMLElement>(null);
    const { isPrivacyPolicyModalOpen } = useAppSelector((state) => state.global);
    const isModalOpen = !cookies?.raia_privacy_policy_read_date || isPrivacyPolicyModalOpen;
    const dispatch = useAppDispatch();

    const onCloseModal = () => {
        dispatch(setPrivacyPolicyModalOpen(false));
        setCookies('raia_privacy_policy_read_date', new Date().toISOString(), {
            maxAge: 30 * 24 * 60 * 60,
            sameSite: 'lax',
        });
    };

    const handleAcknowledgeChange = (_event: ChangeEvent<HTMLInputElement>, checked: boolean) =>
        setCheckbox(checked);

    const handleScroll = () => {
        const content = contentRef.current;
        if (content) {
            const isAtBottom = content.scrollTop + content.clientHeight + 5 > content.scrollHeight;
            if (isAtBottom) {
                setIsContentRead(true);
            }
        }
    };

    return (
        <BasicModal open={isModalOpen}>
            <Fade timeout={500} in={isModalOpen}>
                <Box className="default-settings-modal">
                    <Typography className="modal-title" variant="h6" component="h2">
                        {t('web_app.home.settings.privacy_policy.title')}
                    </Typography>
                    <Box
                        onScroll={handleScroll}
                        ref={contentRef}
                        className="content"
                        data-testid="content"
                    >
                        <Typography variant="body2">
                            {parse(t('web_app.home.settings.privacy_policy.body_1'))}
                        </Typography>
                        <br />
                        <Typography variant="body2">
                            {t('web_app.home.settings.privacy_policy.body_2')}
                        </Typography>
                        <br />
                        <Typography variant="body2">
                            {t('web_app.home.settings.privacy_policy.body_3')}
                        </Typography>
                        <br />
                        <ol>
                            {[...Array(4)].map((_, index) => {
                                const translationKey = `web_app.home.settings.privacy_policy.term_${index + 1}`;
                                return (
                                    <li key={translationKey}>
                                        <Typography variant="body2">
                                            {parse(t(translationKey))}
                                        </Typography>
                                    </li>
                                );
                            })}
                        </ol>
                        <br />
                        <Typography variant="body2">
                            {t('web_app.home.settings.privacy_policy.body_4')}
                        </Typography>
                        <br />
                        <ul>
                            {[...Array(5)].map((_, index) => {
                                const translationKey = `web_app.home.settings.privacy_policy.acknowledgment_${index + 1}`;
                                return (
                                    <li key={translationKey}>
                                        <Typography variant="body2">{t(translationKey)}</Typography>
                                    </li>
                                );
                            })}
                        </ul>
                        <br />
                        <Typography variant="body2">
                            {t('web_app.home.settings.privacy_policy.body_5')}
                        </Typography>
                    </Box>
                    <FormControlLabel
                        disabled={!isContentRead}
                        control={<Checkbox onChange={handleAcknowledgeChange} checked={checkbox} />}
                        label={t(
                            'web_app.home.settings.privacy_policy.acknowledgment_confirmation',
                        )}
                    />
                    <PrimaryButton
                        disabled={!checkbox}
                        className="filled"
                        text={t('home.confirmation_modal_title')}
                        onClick={onCloseModal}
                    ></PrimaryButton>
                </Box>
            </Fade>
        </BasicModal>
    );
};

export default PrivacyPolicyModal;
